import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { createHtmlElement, empty, htmlStringToElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CjsInputTextEditor } from "@cervval.js/client-html5-ui/src/editors/inputTextEditor/inputTextEditor";
import { I18nDigitCode } from "./digitCodeComponent.i18n";

export interface DigitCodeOptions {
    /** Number of digits */
    nbrDigits: number;
    /** Function to call when all digits are full */
    onComplete: (full: boolean) => void;
    /** Title */
    inputName?: string;
    /** Input type */
    inputType: "tel" | "text";
}

export const defaultDigitCodeOptions: DigitCodeOptions = {
    nbrDigits: 4,
    onComplete: () => { },
    inputType: "tel"
};

@CustomElement({
    name: "digit-code-component",
    style: require("./digitCodeComponent.scss"),
    template: require("./digitCodeComponent.html"),
    i18n: require("./digitCodeComponent.i18n").I18n
})
export class DigitCodeComponent<I18n extends I18nDigitCode = I18nDigitCode> extends WebComponent<I18n> {

    @TypedWebComponentOptions(defaultDigitCodeOptions)
    public readonly options!: WebComponentOptions<DigitCodeOptions>;

    private inputs: HTMLInputElement[] = [];

    private finalCode: string = "";

    /** Custom event emitted when an input is made on component */
    @Event(NoBubblesEventInit)
    public inputEvent!: EventHandler<void>;

    /** components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInputTextEditor] })
    public readonly components!: {
        digitCodeContainer: HTMLDivElement,
        name: HTMLDivElement
    };

    constructor(options?: Partial<DigitCodeOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Refresh of the component
     */
    public override refresh(): void {
        this._render();
    }

    /**
     * Render component
     */
    protected _render(): void {
        empty(this.components.digitCodeContainer);
        empty(this.components.name);
        if (this.options.inputName) {
            const label = htmlStringToElement(`<label class="form-label">${this.options.inputName}</label>`);
            if (label) {
                this.components.name.append(label);
            }
        }
        this.inputs = [];

        for (let i = 0; i < this.options.nbrDigits; i++) {
            const input = createHtmlElement("input", {
                attributes: { "type": this.options.inputType, "max-length": "1", "placeholder": "_" },
                cssClass: ["digit-code", `digit-${i}`]
            });

            // Event listener to handle value input
            input.addEventListener("input", (e) => this._handleInput(i));

            // Event listener for backspace
            input.addEventListener("keydown", (e) => this._handleBackSpace(i, e));

            this.components.digitCodeContainer.append(input);
            this.inputs.push(input);
        }

        // Focus the first input initially
        this.inputs[0].focus();
    }

    /** 
     * Handle input event for input component.
     * 
     * Every input that is not a numeric character will reset the input component value to `""`.
     * 
     * A numeric character input will be saved to the component and :
     * - if there is another input after the current component, the focus will be put on it
     * - if the code is complete, `finalCode` will be updated, and the callback `onComplete` will be called with the value `true`
     * 
     * @param index Index of the input component within the `DigitCodeComponent`.
     * Depending on input index, certain interactions are possible
     */
    protected _handleInput(index: number): void {
        const value = this.inputs[index].value;
        if (this.options.inputType !== "tel" || /^\d$/.test(value)) { // Only allow numeric input

            // Move to the next input if it’s not the last one
            if (index < this.options.nbrDigits - 1) {
                this.inputs[index + 1].focus();
            }

            const allValues = this.inputs.map((input) => input.value);
            // Check if all fields are filled
            if (allValues.every((val) => val !== "")) {
                const finalCode = allValues.join("");
                this.finalCode = finalCode;
                this.options.onComplete(true);
            }
        } else {
            // Clear invalid input
            this.inputs[index].value = "";
        }
        this.inputEvent.emit();
    }

    /**
     * Handle the press of the Backspace touch.
     * 
     * A press on the backspace touch will call the callback `onComplete` with the value `false`,
     * and if the input was already empty and there is another input component before it, the focus will be set to this component.
     * 
     * @param index Index of the input component within the `DigitCodeComponent`.
     * Depending on input index, certain interactions are possible
     * @param e Keyboard event
     */
    private _handleBackSpace(index: number, e: KeyboardEvent): void {
        if (e.key === "Backspace") {
            this.options.onComplete(false);
            if (this.inputs[index].value === "" && index > 0) {
                // Move to the previous input on backspace if the current input is empty
                this.inputs[index - 1].focus();
            }
        }
    }

    /**
     * Retrieve final value
     * @returns 
     */
    public getValue(): string {
        return this.finalCode;
    }

    /** Focus the first input of the component */
    public focusFirstInput(): void {
        this.inputs[0].focus();
    }

    /** Reset all inputs of component and set `finalCode` value to `""` */
    public reset(): void {
        for (const input of this.inputs) {
            input.value = "";
        }
        this.finalCode = "";
    }

}